import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { GENDERS_BY_NAME } from 'src/common/constants/genders';
import {
  getFinishTimeBucketScheme,
  resolveFinishTimeBuckets,
} from 'src/helpers/statisticExtras';
import { minutesToHours } from 'src/helpers/dateTimeFormatters';
import { EAgeGroups, TStatistic } from 'src/interfaces/Statistics';

ChartJS.register(BarElement, CategoryScale, LinearScale, Legend, Tooltip);

interface Props {
  statistic: TStatistic;
  distanceKm?: number;
}

export default function PerformanceSection({ statistic, distanceKm }: Props) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { checkinsByGenderByAgeKeys, dataByGenderByAge } = statistic;
  const finishTimeBuckets = resolveFinishTimeBuckets(statistic, distanceKm);
  const bucketUnit = getFinishTimeBucketScheme(distanceKm).unit;

  const timeByAge = useMemo(() => {
    const labels = Object.values(EAgeGroups).map(key => t('statistics.keyAgeGroup', { key }));
    const datasets = checkinsByGenderByAgeKeys.flatMap(genderKey => {
      const gender = t(`base.gender.${GENDERS_BY_NAME[genderKey]?.name ?? genderKey}`);
      const byAge = dataByGenderByAge[genderKey] ?? {};
      return [
        {
          label: t('statistics.bestTimeLabel', { gender }),
          data: Object.values(EAgeGroups).map(age => byAge[age]?.bestTime ?? 0),
          backgroundColor: theme.palette.primary.main,
        },
        {
          label: t('statistics.averageTimeLabel', { gender }),
          data: Object.values(EAgeGroups).map(age => byAge[age]?.averageTime ?? 0),
          backgroundColor: theme.palette.secondary.main,
        },
      ];
    });
    return { labels, datasets };
  }, [
    checkinsByGenderByAgeKeys,
    dataByGenderByAge,
    t,
    theme.palette.primary.main,
    theme.palette.secondary.main,
  ]);

  const histogram = useMemo(() => {
    const buckets = finishTimeBuckets;
    return {
      labels: buckets.map(b =>
        bucketUnit === 'hours'
          ? t('insights.performance.bucketHours', { range: b.label })
          : t('insights.performance.bucketMinutes', { range: b.label }),
      ),
      datasets: [
        {
          label: t('insights.performance.finishDistribution'),
          data: buckets.map(b => b.count),
          backgroundColor: buckets.map((_, index) =>
            index % 2 === 0 ? theme.palette.primary.main : theme.palette.secondary.main,
          ),
        },
      ],
    };
  }, [
    bucketUnit,
    finishTimeBuckets,
    t,
    theme.palette.primary.main,
    theme.palette.secondary.main,
  ]);

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {t('insights.performance.timeByAge')}
        </Typography>
        <Box sx={{ height: 300, width: '100%' }}>
          <Bar
            data={timeByAge}
            options={{
              maintainAspectRatio: false,
              responsive: true,
              plugins: {
                legend: { position: 'bottom' },
                tooltip: {
                  callbacks: {
                    label: ({ dataset, raw }) =>
                      `${dataset.label ?? ''}: ${minutesToHours(raw as number, t)}`,
                  },
                },
              },
              scales: {
                y: {
                  ticks: {
                    callback: value => minutesToHours(Number(value), t),
                  },
                },
              },
            }}
          />
        </Box>
      </Grid>
      <Grid size={{ xs: 12 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {t('insights.performance.finishDistribution')}
        </Typography>
        <Box sx={{ height: 280, width: '100%' }}>
          <Bar
            data={histogram}
            options={{
              maintainAspectRatio: false,
              responsive: true,
              plugins: { legend: { display: false } },
              scales: { y: { beginAtZero: true, ticks: { stepSize: 1 } } },
            }}
          />
        </Box>
      </Grid>
    </Grid>
  );
}
